import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Shield, Building, Calendar,
  Coins, BarChart3, Users, Clock, ExternalLink, FileText
} from 'lucide-react';
import api from '../../lib/api';
import { ProofOfReserveBadge } from '../../components/tokenization/ProofOfReserveBadge';
import { PriceChart } from '../../components/tokenization/PriceChart';
import { OrderBookDisplay } from '../../components/tokenization/OrderBookDisplay';
import { YieldCalculator } from '../../components/tokenization/YieldCalculator';

interface TokenDetail {
  id: string;
  name: string;
  symbol: string;
  description: string;
  category: string;
  issuer: {
    id: string;
    name: string;
    cnpj: string;
  };
  unitPrice: number;
  priceChange24h: number;
  priceChange7d: number;
  totalSupply: number;
  circulatingSupply: number;
  marketCap: number;
  volume24h: number;
  yieldRate: number | null;
  yieldFrequency: string | null;
  maturityDate: string | null;
  lockUpPeriod: number | null;
  minInvestment: number;
  maxInvestment: number | null;
  porCoverage: number;
  porLastUpdate: string;
  contractAddress: string | null;
  blockchain: string;
  tokenStandard: string;
  transferable: boolean;
  whitelistRequired: boolean;
  totalHolders: number;
  documents: Array<{
    type: string;
    name: string;
    url: string;
  }>;
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundo',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios',
};

export function TokenDetails() {
  const { tokenId } = useParams<{ tokenId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'orderbook' | 'calculator'>('overview');

  const { data: token, isLoading } = useQuery({
    queryKey: ['token-details', tokenId],
    queryFn: async () => {
      const res = await api.get(`/tokenization/tokens/${tokenId}`);
      return res.data as TokenDetail;
    },
    enabled: !!tokenId,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatCompact = (value: number) => {
    if (value >= 1000000000) return `R$ ${(value / 100000000000).toFixed(2)}B`;
    if (value >= 1000000) return `R$ ${(value / 100000000).toFixed(2)}M`;
    if (value >= 1000) return `R$ ${(value / 100000).toFixed(2)}K`;
    return formatCurrency(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="text-center py-12">
        <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
        <p className="text-surface-400">Token não encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/banking/tokens')}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{token.name}</h1>
            <span className="text-lg text-surface-400">{token.symbol}</span>
            <ProofOfReserveBadge coverage={token.porCoverage} />
          </div>
          <p className="text-surface-400">{categoryLabels[token.category] || token.category}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/banking/tokens/${tokenId}/sell`)}
            className="px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors"
          >
            Vender
          </button>
          <button
            onClick={() => navigate(`/banking/tokens/${tokenId}/buy`)}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            Comprar
          </button>
        </div>
      </div>

      {/* Price Info */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
        <div className="flex items-end gap-4 mb-4">
          <span className="text-4xl font-bold">{formatCurrency(token.unitPrice)}</span>
          <div className={`flex items-center gap-1 text-lg ${
            token.priceChange24h >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {token.priceChange24h >= 0 ? (
              <TrendingUp className="w-5 h-5" />
            ) : (
              <TrendingDown className="w-5 h-5" />
            )}
            <span>{Math.abs(token.priceChange24h).toFixed(2)}%</span>
            <span className="text-surface-400 text-sm ml-1">24h</span>
          </div>
          <div className={`flex items-center gap-1 ${
            token.priceChange7d >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            <span>{token.priceChange7d >= 0 ? '+' : ''}{token.priceChange7d.toFixed(2)}%</span>
            <span className="text-surface-400 text-sm">7d</span>
          </div>
        </div>

        {/* Price Chart */}
        <PriceChart data={[]} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-800">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-surface-400 hover:text-white'
          }`}
        >
          Visão Geral
        </button>
        <button
          onClick={() => setActiveTab('orderbook')}
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'orderbook'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-surface-400 hover:text-white'
          }`}
        >
          Livro de Ofertas
        </button>
        <button
          onClick={() => setActiveTab('calculator')}
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'calculator'
              ? 'border-primary-500 text-primary-400'
              : 'border-transparent text-surface-400 hover:text-white'
          }`}
        >
          Calculadora
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
                <p className="text-sm text-surface-400 mb-1">Market Cap</p>
                <p className="text-lg font-semibold">{formatCompact(token.marketCap)}</p>
              </div>
              <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
                <p className="text-sm text-surface-400 mb-1">Volume 24h</p>
                <p className="text-lg font-semibold">{formatCompact(token.volume24h)}</p>
              </div>
              <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
                <p className="text-sm text-surface-400 mb-1">Supply Circulante</p>
                <p className="text-lg font-semibold">{token.circulatingSupply.toLocaleString('pt-BR')}</p>
              </div>
              <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
                <p className="text-sm text-surface-400 mb-1">Holders</p>
                <p className="text-lg font-semibold">{token.totalHolders.toLocaleString('pt-BR')}</p>
              </div>
            </div>

            {/* Description */}
            {token.description && (
              <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
                <h3 className="font-semibold mb-3">Sobre o Token</h3>
                <p className="text-surface-300">{token.description}</p>
              </div>
            )}

            {/* Token Info */}
            <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
              <h3 className="font-semibold mb-4">Informações do Token</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-surface-400">Blockchain</p>
                  <p className="font-medium">{token.blockchain}</p>
                </div>
                <div>
                  <p className="text-sm text-surface-400">Padrão</p>
                  <p className="font-medium">{token.tokenStandard}</p>
                </div>
                <div>
                  <p className="text-sm text-surface-400">Transferível</p>
                  <p className="font-medium">{token.transferable ? 'Sim' : 'Não'}</p>
                </div>
                <div>
                  <p className="text-sm text-surface-400">Whitelist</p>
                  <p className="font-medium">{token.whitelistRequired ? 'Obrigatória' : 'Não requerida'}</p>
                </div>
                {token.contractAddress && (
                  <div className="col-span-2">
                    <p className="text-sm text-surface-400">Contrato</p>
                    <a
                      href={`https://etherscan.io/address/${token.contractAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm text-primary-400 hover:underline flex items-center gap-1"
                    >
                      {token.contractAddress.slice(0, 10)}...{token.contractAddress.slice(-8)}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Documents */}
            {token.documents.length > 0 && (
              <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
                <h3 className="font-semibold mb-4">Documentos</h3>
                <div className="space-y-2">
                  {token.documents.map((doc, index) => (
                    <a
                      key={index}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-surface-800 rounded-lg hover:bg-surface-700 transition-colors"
                    >
                      <FileText className="w-5 h-5 text-surface-400" />
                      <div className="flex-1">
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm text-surface-400">{doc.type}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-surface-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Investment Info */}
            <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
              <h3 className="font-semibold mb-4">Investimento</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-surface-400">Mínimo</span>
                  <span className="font-medium">{formatCurrency(token.minInvestment)}</span>
                </div>
                {token.maxInvestment && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Máximo</span>
                    <span className="font-medium">{formatCurrency(token.maxInvestment)}</span>
                  </div>
                )}
                {token.yieldRate && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Rendimento</span>
                    <span className="font-medium text-green-400">{token.yieldRate.toFixed(2)}% a.a.</span>
                  </div>
                )}
                {token.yieldFrequency && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Pagamento</span>
                    <span className="font-medium">{token.yieldFrequency}</span>
                  </div>
                )}
                {token.maturityDate && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Vencimento</span>
                    <span className="font-medium">{formatDate(token.maturityDate)}</span>
                  </div>
                )}
                {token.lockUpPeriod && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Lock-up</span>
                    <span className="font-medium">{token.lockUpPeriod} dias</span>
                  </div>
                )}
              </div>
            </div>

            {/* Issuer */}
            <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-primary-400" />
                Emissor
              </h3>
              <p className="font-medium">{token.issuer.name}</p>
              <p className="text-sm text-surface-400">CNPJ: {token.issuer.cnpj}</p>
            </div>

            {/* PoR Info */}
            <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-400" />
                Proof of Reserve
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-surface-400">Cobertura</span>
                  <ProofOfReserveBadge coverage={token.porCoverage} />
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Última Atualização</span>
                  <span className="text-sm">{formatDate(token.porLastUpdate)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'orderbook' && (
        <OrderBookDisplay bids={[]} asks={[]} />
      )}

      {activeTab === 'calculator' && token.yieldRate && (
        <YieldCalculator
          tokenPrice={token.unitPrice}
          annualYield={token.yieldRate}
          frequency={(token.yieldFrequency as 'MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL' | 'AT_MATURITY') || 'MONTHLY'}
          maturityDate={token.maturityDate ?? undefined}
        />
      )}
    </div>
  );
}

export default TokenDetails;
